import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import type { BlockedReason } from '@/types/maintenance';

const blockedReasonOptions: { value: BlockedReason; label: string }[] = [
  { value: 'parts', label: 'Parts' },
  { value: 'qa_hold', label: 'QA Hold' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'waiting_approval', label: 'Waiting Approval' },
  { value: 'no_manpower', label: 'No Manpower' },
  { value: 'other', label: 'Other' },
];

interface MarkBlockedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    blocked_reason: BlockedReason;
    blocked_reason_text?: string;
    expected_unblock_date?: string;
    blocked_notes?: string;
  }) => void;
}

export function MarkBlockedDialog({ open, onOpenChange, onConfirm }: MarkBlockedDialogProps) {
  const [reason, setReason] = useState<BlockedReason | undefined>(undefined);
  const [reasonText, setReasonText] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');

  const isValid = !!reason && (reason !== 'other' || reasonText.trim().length > 0);

  const handleSubmit = () => {
    if (!reason) return;
    onConfirm({
      blocked_reason: reason,
      blocked_reason_text: reason === 'other' ? reasonText : undefined,
      expected_unblock_date: expectedDate || undefined,
      blocked_notes: notes || undefined,
    });
    // Reset
    setReason(undefined);
    setReasonText('');
    setExpectedDate('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-status-red" />
            Mark Blocked
          </DialogTitle>
          <DialogDescription>
            Record the reason this work order is blocked.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Block Reason *</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as BlockedReason)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {blockedReasonOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reason === 'other' && (
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="Describe the blocking reason..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Expected Unblock Date</Label>
            <Input
              type="datetime-local"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="min-h-[60px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!isValid}>
            Mark Blocked
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
